import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Form, Table, Modal, Alert } from 'react-bootstrap';
import { Calendar, Clock, People, Plus, PencilSquare, Trash, ToggleOn, ToggleOff } from 'react-bootstrap-icons';
import PageLayout from '../../layouts/PageLayout';
import { useBranch, useBranchAPI } from '../../hooks';
import { shiftTemplateAPI, taskAPI } from '../../services';
import { toast } from 'react-toastify';
import TaskAssignmentModal from '../../components/modals/TaskAssignmentModal';

export default function ShiftTemplatePage() {
  const { selectedBranch } = useBranch();
  const { withBranch } = useBranchAPI();

  const [templates, setTemplates] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [togglingStatus, setTogglingStatus] = useState({});

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Form states
  const defaultBonusByDay = Array(7).fill(0);
  const [formData, setFormData] = useState({
    name: '',
    start_hour: 8,
    start_minute: 0,
    duration_minutes: 240,
    max_employees: 1,
    reward_rate_bonus: 0,
    reward_rate_bonus_by_day: [...defaultBonusByDay],
    days_of_week: [],
    is_active: true,
    is_cashier: false,
    is_qc: false,
  });

  const dayNames = ['Chủ nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'];

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await withBranch(shiftTemplateAPI.getShiftTemplates, {
        page: 1,
        limit: 100,
      });
      setTemplates(response.data || response);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Không thể tải danh sách cấu hình ca làm việc');
    } finally {
      setLoading(false);
    }
  }, [withBranch]);

  const loadTasks = useCallback(async () => {
    try {
      const response = await taskAPI.getTasks({
        page: 1,
        limit: 100,
        is_active: true,
      });
      setTasks(response.data || response);
    } catch (err) {
      console.error('Error loading tasks:', err);
    }
  }, []);

  // Load data
  useEffect(() => {
    loadTemplates();
    loadTasks();
  }, [selectedBranch?.id, loadTemplates, loadTasks]);

  const handleCreateTemplate = async () => {
    try {
      setLoading(true);

      await withBranch(shiftTemplateAPI.createShiftTemplate, formData);

      toast.success('Tạo cấu hình ca làm việc thành công!');
      setShowCreateModal(false);
      resetForm();
      loadTemplates();
    } catch (err) {
      console.error('Error creating template:', err);
      toast.error('Không thể tạo cấu hình ca làm việc');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTemplate = async () => {
    try {
      setLoading(true);

      await withBranch(
        async (dataWithBranch) => shiftTemplateAPI.updateShiftTemplate(selectedTemplate.id, dataWithBranch),
        formData,
      );

      toast.success('Cập nhật cấu hình ca làm việc thành công!');
      setShowEditModal(false);
      resetForm();
      loadTemplates();
    } catch (err) {
      console.error('Error updating template:', err);
      toast.error('Không thể cập nhật cấu hình ca làm việc');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa cấu hình ca làm việc này?')) return;

    try {
      await shiftTemplateAPI.deleteShiftTemplate(templateId);
      toast.success('Xóa cấu hình ca làm việc thành công!');
      loadTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      toast.error('Không thể xóa cấu hình ca làm việc');
    }
  };

  const handleToggleActive = async (template) => {
    const templateId = template.id;
    const newActiveState = !template.is_active;

    try {
      setTogglingStatus((prev) => ({ ...prev, [templateId]: true }));
      await withBranch(async (dataWithBranch) => shiftTemplateAPI.updateShiftTemplate(templateId, dataWithBranch), {
        is_active: newActiveState,
      });
      toast.success(`Đã ${newActiveState ? 'bật' : 'tắt'} cấu hình ca làm việc`);
      loadTemplates();
    } catch (err) {
      console.error('Error toggling template status:', err);
      toast.error('Thay đổi trạng thái thất bại');
    } finally {
      setTogglingStatus((prev) => ({ ...prev, [templateId]: false }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      start_hour: 8,
      start_minute: 0,
      duration_minutes: 240,
      max_employees: 1,
      reward_rate_bonus: 0,
      reward_rate_bonus_by_day: [...defaultBonusByDay],
      days_of_week: [],
      is_active: true,
      is_cashier: false,
      is_qc: false,
    });
  };

  const openEditModal = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      start_hour: template.start_hour,
      start_minute: template.start_minute,
      duration_minutes: template.duration_minutes,
      max_employees: template.max_employees,
      reward_rate_bonus: template.reward_rate_bonus ?? 0,
      reward_rate_bonus_by_day: Array.isArray(template.reward_rate_bonus_by_day)
        ? template.reward_rate_bonus_by_day.map((value, idx) => Number(value) || 0)
        : [...defaultBonusByDay],
      days_of_week: template.days_of_week || [],
      is_active: template.is_active,
      is_cashier: template.is_cashier || false,
      is_qc: template.is_qc || false,
    });
    setShowEditModal(true);
  };

  const openTaskModal = (template) => {
    setSelectedTemplate(template);
    setShowTaskModal(true);
  };

  const handleTaskModalSave = () => {
    loadTemplates();
  };

  const formatTime = (hour, minute) => {
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? mins + 'm' : ''}`;
  };

  const getDaysText = (days) => {
    if (!days || days.length === 0) return 'Không có';
    return days.map((day) => dayNames[day]).join(', ');
  };

  const handleDayChange = (dayIndex, checked) => {
    const newDays = checked
      ? [...formData.days_of_week, dayIndex]
      : formData.days_of_week.filter((d) => d !== dayIndex);

    setFormData({ ...formData, days_of_week: newDays });
  };

  const handleSelectAllDays = () => {
    const allDays = [0, 1, 2, 3, 4, 5, 6];
    const isAllSelected = allDays.every((day) => formData.days_of_week.includes(day));

    setFormData({
      ...formData,
      days_of_week: isAllSelected ? [] : allDays,
    });
  };

  const handleBonusRateChange = (dayIndex, value) => {
    const next = [...(formData.reward_rate_bonus_by_day || defaultBonusByDay)];
    next[dayIndex] = Math.max(0, Math.min(100, Number(value) || 0));
    setFormData({ ...formData, reward_rate_bonus_by_day: next });
  };

  const totalCount = templates.length;
  const activeCount = templates.filter((t) => t.is_active).length;
  const inactiveCount = totalCount - activeCount;


  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header">
          <div className="mc-page-header-title">
            <h1>Cấu hình ca làm việc</h1>
            <p>Quản lý các cấu hình ca làm việc định kỳ cho chi nhánh</p>
          </div>
          <div className="mc-page-header-actions">
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
              disabled={loading}
            >
              <Plus className="me-2" />
              Tạo mẫu mới
            </Button>
          </div>
        </div>

        {/* Summary — khổ điện thoại: 1 dải gọn 3 số, không dùng 3 thẻ xếp chồng */}
        <div className="mc-stat-strip d-md-none">
          <div className="mc-stat-strip-item">
            <Calendar
              className="text-primary"
              size={16}
            />
            <span className="mc-stat-strip-value">{totalCount}</span>
            <span className="mc-stat-strip-label">Tổng ca</span>
          </div>
          <div className="mc-stat-strip-item">
            <ToggleOn
              className="text-success"
              size={16}
            />
            <span className="mc-stat-strip-value">{activeCount}</span>
            <span className="mc-stat-strip-label">Hoạt động</span>
          </div>
          <div className="mc-stat-strip-item">
            <ToggleOff
              className="text-warning"
              size={16}
            />
            <span className="mc-stat-strip-value">{inactiveCount}</span>
            <span className="mc-stat-strip-label">Tạm dừng</span>
          </div>
        </div>

        {/* Summary — khổ máy tính: giữ nguyên 3 thẻ như cũ */}
        <Row className="mb-4 d-none d-md-flex">
          <Col md={3}>
            <Card className="mc-card">
              <Card.Body className="text-center">
                <Calendar
                  className="text-primary mb-2"
                  size={32}
                />
                <h4>{totalCount}</h4>
                <p className="text-muted mb-0">Tổng cấu hình ca</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="mc-card">
              <Card.Body className="text-center">
                <ToggleOn
                  className="text-success mb-2"
                  size={32}
                />
                <h4>{activeCount}</h4>
                <p className="text-muted mb-0">Đang hoạt động</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="mc-card">
              <Card.Body className="text-center">
                <ToggleOff
                  className="text-warning mb-2"
                  size={32}
                />
                <h4>{inactiveCount}</h4>
                <p className="text-muted mb-0">Tạm dừng</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Templates Table */}
        <Card className="mc-card">
          <Card.Header>
            <h5 className="mb-0">Danh sách cấu hình ca</h5>
          </Card.Header>
          <Card.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            {loading ? (
              <div className="text-center py-4">
                <div
                  className="spinner-border"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <Table
                responsive
                hover
              >
                <thead>
                  <tr>
                    <th>Tên mẫu</th>
                    <th>Thời gian</th>
                    <th>Thời lượng</th>
                    <th>Ngày trong tuần</th>
                    <th>Số nhân viên tối đa</th>
                    <th>Tỉ lệ nhận thưởng (%)</th>
                    <th>Trạng thái</th>
                    <th>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((template) => (
                    <tr key={template.id}>
                      <td>
                        <strong>{template.name}</strong>
                      </td>
                      <td>
                        <Clock className="me-1" />
                        {formatTime(template.start_hour, template.start_minute)}
                      </td>
                      <td>{formatDuration(template.duration_minutes)}</td>
                      <td>
                        <small>{getDaysText(template.days_of_week)}</small>
                      </td>
                      <td>
                        <People className="me-1" />
                        {template.max_employees}
                      </td>
                      <td>{template.reward_rate_bonus ?? 0}%</td>
                      <td>
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <Form.Check
                            type="switch"
                            id={`status-switch-${template.id}`}
                            checked={template.is_active}
                            onChange={() => handleToggleActive(template)}
                            disabled={togglingStatus[template.id]}
                          />
                          {togglingStatus[template.id] ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            <span className="small text-muted">{template.is_active ? 'Hoạt động' : 'Tạm dừng'}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => openEditModal(template)}
                          >
                            <PencilSquare />
                          </Button>

                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => openTaskModal(template)}
                          >
                            Gán task
                            {Array.isArray(template.tasks) && (
                              <span className="badge bg-primary ms-2">{template.tasks.length}</span>
                            )}
                          </Button>

                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDeleteTemplate(template.id)}
                          >
                            <Trash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Create Template Modal */}
        <Modal
          show={showCreateModal}
          onHide={() => setShowCreateModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Tạo cấu hình ca mới</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Tên cấu hình ca *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ví dụ: Ca sáng, Ca chiều..."
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Giờ bắt đầu *</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="23"
                    value={formData.start_hour}
                    onChange={(e) => setFormData({ ...formData, start_hour: parseInt(e.target.value) })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Phút bắt đầu *</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="59"
                    value={formData.start_minute}
                    onChange={(e) => setFormData({ ...formData, start_minute: parseInt(e.target.value) })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Thời lượng (phút) *</Form.Label>
                  <Form.Control
                    type="number"
                    min="60"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Số nhân viên tối đa *</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={formData.max_employees}
                    onChange={(e) => setFormData({ ...formData, max_employees: parseInt(e.target.value) })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Trạng thái</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Check
                      type="switch"
                      id="create-is-active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      label={formData.is_active ? 'Hoạt động' : 'Tạm dừng'}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tỉ lệ nhận thưởng (%)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    value={formData.reward_rate_bonus ?? 0}
                    onChange={(e) => setFormData({ ...formData, reward_rate_bonus: parseInt(e.target.value) || 0 })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ca thu ngân</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Check
                      type="switch"
                      id="create-is-cashier"
                      checked={formData.is_cashier || false}
                      onChange={(e) => setFormData({ ...formData, is_cashier: e.target.checked })}
                      label={formData.is_cashier ? 'Có thu ngân' : 'Không thu ngân'}
                    />
                  </div>
                  {formData.is_cashier && (
                    <Alert variant="info" className="mt-2 small">
                      <strong>Lưu ý:</strong> Ca thu ngân yêu cầu nhân viên nhập số tiền mặt đầu ca và cuối ca khi check-in/checkout.
                    </Alert>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ca QC</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Check
                      type="switch"
                      id="create-is-qc"
                      checked={formData.is_qc || false}
                      onChange={(e) => setFormData({ ...formData, is_qc: e.target.checked })}
                      label={formData.is_qc ? 'Ca QC' : 'Không phải ca QC'}
                    />
                  </div>
                  {formData.is_qc && (
                    <Alert variant="info" className="mt-2 small">
                      <strong>Lưu ý:</strong> Ca QC có thể review task với video bằng chứng (5-10s).
                    </Alert>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="mb-0">Ngày trong tuần *</Form.Label>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleSelectAllDays}
                    >
                      {[0, 1, 2, 3, 4, 5, 6].every((day) => formData.days_of_week.includes(day))
                        ? 'Bỏ chọn tất cả'
                        : 'Chọn tất cả'}
                    </Button>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {dayNames.map((day, index) => (
                      <Form.Check
                        key={index}
                        type="checkbox"
                        id={`day-${index}`}
                        label={day}
                        checked={formData.days_of_week.includes(index)}
                        onChange={(e) => handleDayChange(index, e.target.checked)}
                      />
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-0">Tỉ lệ thưởng theo ngày trong tuần (%)</Form.Label>
                  <div className="d-flex flex-wrap gap-3 mt-2">
                    {dayNames.map((day, index) => (
                      <div
                        key={index}
                        className="d-flex flex-column"
                        style={{ minWidth: '120px' }}
                      >
                        <span className="text-muted small mb-1">{day}</span>
                        <div className="d-flex gap-1 align-items-center">
                          <Form.Control
                            type="number"
                            size="sm"
                            min="0"
                            max="100"
                            value={formData.reward_rate_bonus_by_day?.[index] ?? 0}
                            onChange={(e) => handleBonusRateChange(index, e.target.value)}
                          />
                          <span className="small">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Hủy
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateTemplate}
              disabled={loading}
            >
              {loading ? 'Đang tạo...' : 'Tạo mẫu'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Template Modal */}
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Chỉnh sửa cấu hình ca</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Tên cấu hình ca *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Giờ bắt đầu *</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="23"
                    value={formData.start_hour}
                    onChange={(e) => setFormData({ ...formData, start_hour: parseInt(e.target.value) })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Phút bắt đầu *</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="59"
                    value={formData.start_minute}
                    onChange={(e) => setFormData({ ...formData, start_minute: parseInt(e.target.value) })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Thời lượng (phút) *</Form.Label>
                  <Form.Control
                    type="number"
                    min="60"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Số nhân viên tối đa *</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={formData.max_employees}
                    onChange={(e) => setFormData({ ...formData, max_employees: parseInt(e.target.value) })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Trạng thái</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Check
                      type="switch"
                      id="create-is-active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      label={formData.is_active ? 'Hoạt động' : 'Tạm dừng'}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tăng thưởng (%)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    value={formData.reward_rate_bonus}
                    onChange={(e) => setFormData({ ...formData, reward_rate_bonus: parseInt(e.target.value) || 0 })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ca thu ngân</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Check
                      type="switch"
                      id="edit-is-cashier"
                      checked={formData.is_cashier || false}
                      onChange={(e) => setFormData({ ...formData, is_cashier: e.target.checked })}
                      label={formData.is_cashier ? 'Có thu ngân' : 'Không thu ngân'}
                    />
                  </div>
                  {formData.is_cashier && (
                    <Alert variant="info" className="mt-2 small">
                      <strong>Lưu ý:</strong> Ca thu ngân yêu cầu nhân viên nhập số tiền mặt đầu ca và cuối ca khi check-in/checkout.
                    </Alert>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ca QC</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Check
                      type="switch"
                      id="edit-is-qc"
                      checked={formData.is_qc || false}
                      onChange={(e) => setFormData({ ...formData, is_qc: e.target.checked })}
                      label={formData.is_qc ? 'Ca QC' : 'Không phải ca QC'}
                    />
                  </div>
                  {formData.is_qc && (
                    <Alert variant="info" className="mt-2 small">
                      <strong>Lưu ý:</strong> Ca QC có thể review task với video bằng chứng (5-10s).
                    </Alert>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="mb-0">Ngày trong tuần *</Form.Label>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleSelectAllDays}
                    >
                      {[0, 1, 2, 3, 4, 5, 6].every((day) => formData.days_of_week.includes(day))
                        ? 'Bỏ chọn tất cả'
                        : 'Chọn tất cả'}
                    </Button>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {dayNames.map((day, index) => (
                      <Form.Check
                        key={index}
                        type="checkbox"
                        id={`edit-day-${index}`}
                        label={day}
                        checked={formData.days_of_week.includes(index)}
                        onChange={(e) => handleDayChange(index, e.target.checked)}
                      />
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-0">Tỉ lệ thưởng theo ngày trong tuần (%)</Form.Label>
                  <div className="d-flex flex-wrap gap-3 mt-2">
                    {dayNames.map((day, index) => (
                      <div
                        key={index}
                        className="d-flex flex-column"
                        style={{ minWidth: '120px' }}
                      >
                        <span className="text-muted small mb-1">{day}</span>
                        <div className="d-flex gap-1 align-items-center">
                          <Form.Control
                            type="number"
                            size="sm"
                            min="0"
                            max="100"
                            value={formData.reward_rate_bonus_by_day?.[index] ?? 0}
                            onChange={(e) => handleBonusRateChange(index, e.target.value)}
                          />
                          <span className="small">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowEditModal(false)}
            >
              Hủy
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateTemplate}
              disabled={loading}
            >
              {loading ? 'Đang cập nhật...' : 'Cập nhật'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Assign Tasks Modal */}
        <TaskAssignmentModal
          show={showTaskModal}
          onHide={() => setShowTaskModal(false)}
          template={selectedTemplate}
          allTasks={tasks}
          onSave={handleTaskModalSave}
          onTasksUpdate={loadTasks}
        />
      </div>
    </PageLayout>
  );
}
